import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfileQuery } from "@/hooks/profile/useProfileQuery";
import { toast } from "sonner";

interface CreateItemData {
  name: string;
  description?: string;
  price_per_hour: number;
  category_id: string;
  image_url?: string;
}

export const useCreateReservationItem = () => {
  const queryClient = useQueryClient();
  const { data: profile } = useProfileQuery();

  return useMutation({
    mutationFn: async (data: CreateItemData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !profile) throw new Error("Usuario no autenticado");

      // Verify manager has access to the category's gym
      const { data: category, error: categoryError } = await supabase
        .from('reservation_categories')
        .select('gym_id')
        .eq('id', data.category_id)
        .single();

      if (categoryError || !category) {
        console.error('Error fetching category:', categoryError);
        console.error('Category ID:', data.category_id);
        throw new Error("Categoría no encontrada");
      }

      const { data: gymManager } = await supabase
        .from('gym_managers')
        .select('*')
        .eq('manager_id', user.id)
        .eq('gym_id', category.gym_id)
        .single();

      if (!gymManager && profile.role !== 'admin') {
        throw new Error("No tienes permisos para gestionar este gimnasio");
      }

      const { data: item, error } = await supabase
        .from('reservation_items')
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return item;
    },
    onSuccess: (item, variables) => {
      // Invalidate specific category items to match the query key pattern
      queryClient.invalidateQueries({ queryKey: ['reservation-items', variables.category_id] });
      // Also invalidate the general pattern in case other components use it
      queryClient.invalidateQueries({ queryKey: ['reservation-items'] });
      toast.success("Elemento creado exitosamente");
    },
    onError: (error) => {
      toast.error(error.message || "Error al crear el elemento");
    },
  });
};